export const notificationUtils = {
  isSupported() {
    return 'Notification' in window && 'serviceWorker' in navigator;
  },

  async requestPermission() {
    if (!this.isSupported()) {
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      return false;
    }
  },

  getPermissionStatus() {
    if (!this.isSupported()) return 'unsupported';
    return Notification.permission;
  },

  formatTimeAgo(timestamp) {
    const seconds = Math.floor(Date.now() / 1000 - timestamp);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  },

  formatBytes(bytes) {
    if (bytes < 1024) return bytes + ' B';
    const kb = bytes / 1024;
    if (kb < 1024) return kb.toFixed(2) + ' KB';
    const mb = kb / 1024;
    return mb.toFixed(2) + ' MB';
  },

  formatCurrency(amount, currency = 'USD') {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return formatter.format(amount);
  },

  async showNewBlockNotification(blockData, bitcoinPrice) {
    if (!this.isSupported() || Notification.permission !== 'granted') {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const selectedCurrency = localStorage.getItem('selectedCurrency') || 'USD';

      // Calculate values
      const poolName = blockData.extras?.pool?.name || 'Unknown Pool';
      // const blockSize = this.formatBytes(blockData.size);
      const avgFeeRate = Math.round(blockData.extras?.avgFeeRate || 0);
      // const feeRange = blockData.extras?.feeRange || [0, 0];
      // const feeRangeFormatted = `${feeRange[0].toFixed(4)} - ${feeRange[1].toFixed(4)}`;
      const totalFees = blockData.extras?.totalFees || 0;
      const totalFeesBTC = (totalFees / 100000000).toFixed(6);
      const totalFeesUSD = (totalFees / 100000000) * bitcoinPrice;
      const subsidyBTC = 3.125; // Current subsidy
      const subsidyUSD = subsidyBTC * bitcoinPrice;
      const timeAgo = this.formatTimeAgo(blockData.timestamp);

      const title = `Block ${blockData.height.toLocaleString()} Found!`;

      // Build detailed body
      const bodyLines = [
        ' ',
        `⛏️ ${poolName}`,
        `📦 ${blockData.tx_count.toLocaleString()} TX`,
        `⚡ ~${avgFeeRate} sats/vB`,
        // `📊 ${feeRangeFormatted} sats/vB`,
        `💰 Fees: ${totalFeesBTC} BTC • ${this.formatCurrency(totalFeesUSD, selectedCurrency)}`,
        `🎁 Subsidy: ${subsidyBTC} BTC • ${this.formatCurrency(subsidyUSD, selectedCurrency)}`,
        `⏰ Found ${timeAgo}`
      ];

      const options = {
        body: bodyLines.join('\n'),
        icon: '/favicon-192x192.png',
        tag: `block-${blockData.height}`,
        renotify: false,
        requireInteraction: false,
        silent: false,
        timestamp: blockData.timestamp * 1000,
        data: {
          blockHeight: blockData.height,
          blockHash: blockData.id,
          timestamp: blockData.timestamp,
          url: `https://mempool.space/block/${blockData.id}`
        },
        actions: [
          {
            action: 'view',
            title: 'View Block'
          }
        ]
      };

      await registration.showNotification(title, options);
      return true;
    } catch (error) {
      return false;
    }
  },

  async showPriceNotification(currentPrice, targetPrice, direction) {
    if (!this.isSupported() || Notification.permission !== 'granted') {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const selectedCurrency = localStorage.getItem('selectedCurrency') || 'USD';

      const emoji = direction === 'above' ? '📈' : '📉';
      const action = direction === 'above' ? 'crossed above' : 'dropped below';

      const title = `${emoji} Bitcoin Price Alert!`;
      const body = `Bitcoin has ${action} ${this.formatCurrency(targetPrice, selectedCurrency)}\nCurrent price: ${this.formatCurrency(currentPrice, selectedCurrency)}`;

      const options = {
        body: body,
        icon: '/favicon-192x192.png',
        tag: 'price-alert',
        renotify: true,
        requireInteraction: false,
        silent: false,
        timestamp: Date.now(),
        data: {
          type: 'price-alert',
          price: currentPrice,
          targetPrice: targetPrice,
          direction: direction
        }
      };

      await registration.showNotification(title, options);
      return true;
    } catch (error) {
      return false;
    }
  },

  async registerServiceWorker() {
    if (!('serviceWorker' in navigator)) {
      return null;
    }

    try {
      // Check if already registered
      const existingRegistration = await navigator.serviceWorker.getRegistration();
      if (existingRegistration) {
        // Check for updates to the service worker
        existingRegistration.update();
        return existingRegistration;
      }

      const registration = await navigator.serviceWorker.register('/pwabuilder-sw.js');

      // Wait for the service worker to be ready
      await navigator.serviceWorker.ready;

      return registration;
    } catch (error) {
      return null;
    }
  }
};

export const setupNotifications = async () => {
  if (!notificationUtils.isSupported()) {
    return { supported: false, permission: 'unsupported' };
  }

  const registration = await notificationUtils.registerServiceWorker();
  const permission = notificationUtils.getPermissionStatus();

  return {
    supported: true,
    permission,
    registration
  };
};